/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { skipToken } from '@reduxjs/toolkit/query';
import Big from 'big.js';
import Tooltip from 'components/@extended/Tooltip';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader.tsx';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_OTHER_ACTION, CreateDialogModalFrame } from 'components/create-dialog/CreateDialogModalFrame';
import { CreateDialogSection } from 'components/create-dialog/CreateDialogSection';
import { createRecord } from 'components/create-dialog/recordTypes';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput.tsx';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput.tsx';
import HoursInput from 'components/forms/inputs/rhf-inputs/HoursInput';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput.tsx';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput.tsx';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { FormattedDate } from 'components/misc/FormattedDate';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { Field, FieldTitle } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { formatToHours } from 'components/util/HoursDisplay';
import { format, isBefore } from 'date-fns';
import uniqBy from 'lodash/uniqBy';
import { MultiplierType } from 'pages/invoices/util/InvoicesPageUtil';
import { resolveHourlyPriceOn } from 'pages/pricelists/matterRateResolution';
import { ChangeEvent, MutableRefObject, ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { useFindMatterRatesQuery, useLazyGetMattersListQuery } from 'store/api/matters.ts';
import { MatterAssignmentMinDto } from 'types/assignments';
import { ClassifierData } from 'types/classifiers';
import { EmployeeListItemMinDto } from 'types/employees.ts';
import { FeatureFlag } from 'types/features.ts';
import { InvoiceTimecard, InvoiceTimecardForm } from 'types/invoice-details';
import { EditIntentTypeCode, InvoiceData } from 'types/invoices.ts';
import { RemunerationCalcType } from 'types/matters.ts';
import { NamedEntityMinDto } from 'types/other';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations.ts';
import { renderMatterIndicators, renderMatterOption } from 'utils/matterOptionRenderer';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { useCanReadMatterAssignments, useHasPermissions } from 'utils/permissionUtil.ts';
import { hoursToSeconds } from 'utils/time';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';

interface Props {
    onClose: () => void;
    onSave: (formData: InvoiceTimecardForm) => Promise<void>;
    initialData: InvoiceTimecardForm;
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    multiplierTypes: MultiplierType[];
    matterAssingmentList: NamedEntityMinDto[];
    invoiceTimecard: InvoiceTimecard;
    isSubmittingRef?: MutableRefObject<boolean>;
}

/** The sections the new design draws the form in: the timecard's own fields, and what the invoice makes of it. */
const TIMECARD_SECTION = 'timecards.timecard';
const INVOICE_SECTION = 'invoices.invoice';

/** A field alone on a row of the form's 12-column grid, whose gutters are 16px. */
const FULL_ROW = 'col-span-12';

/** A full row tucked 10px under the one above: the negative margin takes 6px back from the gutter. */
const TUCKED_ROW = 'col-span-12 -mt-1.5';

type GroupedMatterAssignment = MatterAssignmentMinDto & { group: string };

function isAssignmentClosedOn(assignment: MatterAssignmentMinDto, timecardDate: string | null) {
    if (!assignment.endDate || !timecardDate) {
        return false;
    }
    return !isBefore(new Date(timecardDate), new Date(assignment.endDate));
}

const InvoiceTimecardEditModal = (props: Props) => {
    const {
        onSave,
        onClose,
        initialData,
        matterAssingmentList,
        classifiers,
        multiplierTypes,
        invoice,
        invoiceTimecard,
        isSubmittingRef,
    } = props;
    const isNewDesign = useIsNewDesign();
    const multiplierLabelId = useId();
    const showEmptySelection = useIsFeatureEnabled(FeatureFlag.SHOW_EMPTY_SELECTION);
    const isExtraOptionsShown = useIsFeatureEnabled(FeatureFlag.INVOICE_TIMECARD_EXTRA_OPTIONS);
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    const isTimecardNotesEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_NOTES);
    const { displayDecimalScale, isTwoDecimalEntryEnabled } = useTimecardPrecision();
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const {
        control,
        setValue,
        getValues,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<InvoiceTimecardForm>({
        defaultValues: initialData,
    });

    // Mirror RHF's isSubmitting flag into a parent-owned ref so the parent can
    // observe the latest value without re-rendering. Not an event-handler effect:
    // isSubmitting is RHF-internal state, not driven by a single onClick.
    useEffect(() => {
        if (isSubmittingRef) {
            isSubmittingRef.current = isSubmitting;
        }
    }, [isSubmitting, isSubmittingRef]);

    const [selectedMultiplierType, setSelectedmultiplierType] = useState<string>(
        initialData.multiplierType?.value || ''
    );

    const { timecard, timecardDate: invoiceTimecardDate } = invoiceTimecard;

    const [
        isNonBillable,
        preserveNonBillableHours,
        timecardActivityType,
        isPositionTextInputEnabled,
        matter,
        remunerationCalcType,
        billedTimeInHours,
        totalTimeInHours,
        isTimecardForInputEnabled,
        watchedHourlyPrice,
        watchedDiscountRate,
        watchedMultiplierRate,
        watchedManualAdjustmentAmount,
    ] = useWatch({
        control,
        name: [
            'isNonBillable',
            'preserveNonBillableHours',
            'timecardActivityType',
            'isPositionTextInputEnabled',
            'matter',
            'remunerationCalcType',
            'billedTimeInHours',
            'totalTimeInHours',
            'isTimecardForInputEnabled',
            'hourlyPrice',
            'discountRate',
            'multiplierRate',
            'manualAdjustmentAmount',
        ],
    });

    // The billing uplift value factors belong to the timecard form only; editing billed time here
    // must not raise the questionnaire. Stored factors still round-trip through the update DTO.

    const [newEmployee, setNewEmployee] = useState<EmployeeListItemMinDto | null>(null);

    const matterIdList = useMemo(() => {
        return invoice?.invoiceMatters?.map((item) => item.matterId) || [];
    }, [invoice?.invoiceMatters]);

    const mattersListFilter = matterIdList.length > 0 ? `id=in=(${matterIdList.join(',')})` : undefined;

    const showActivityTypeSelectInput =
        timecardActivityType !== null ||
        getValidOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']).length > 0 ||
        showEmptySelection;

    const remunerationCalcTypeOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['REMUNERATION_CALC_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? []
        ).map((option) => {
            if (option.value === RemunerationCalcType.EQUALS_BILLED_TIME) {
                return {
                    ...option,
                    label: `${option.label} (${formatToHours(Number(billedTimeInHours), displayDecimalScale)})`,
                };
            } else if (option.value === RemunerationCalcType.EQUALS_USED_TIME) {
                return {
                    ...option,
                    label: `${option.label} (${formatToHours(Number(totalTimeInHours), displayDecimalScale)})`,
                };
            }
            return option;
        });
    }, [classifiers, totalTimeInHours, billedTimeInHours, displayDecimalScale]);

    const isUserPrivilegedToReadRemunerationTime = useHasPermissions([
        'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
        'READ.TIMECARDS.REMUNERATION',
    ]);
    const isUserPrivilegedToUpdateRemunerationTime = useHasPermissions([
        'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
        'UPDATE.INVOICES.REMUNERATION',
    ]);

    const isUserPrivilegedToEnableMirroring = useHasPermissions(['UPDATE.INVOICES.TC_EXP_MIRROR']);
    const isUserPrivilegedToUpdateHourlyRate = useHasPermissions(['UPDATE.INVOICES.TIMECARD_HOURLY_RATE.ALL']);

    const showRemunerationCalcTypeRadioInput =
        useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL) &&
        remunerationCalcTypeOptions.length > 0 &&
        isUserPrivilegedToReadRemunerationTime;

    const onRemunerationTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const hoursValue = event.target.value;

            let secondsValue: number | null = null;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }

            setValue('remunerationTimeInSeconds', secondsValue);
        },
        [setValue]
    );

    const handleCancel = useCallback(() => {
        if (!isSubmitting) onClose();
    }, [isSubmitting, onClose]);

    const handleOnSave = handleSubmit(async (data) => {
        try {
            await onSave(data);
            if (isSubmittingRef) isSubmittingRef.current = false;
            onClose();
        } catch (e) {
            console.error('onSubmit error: ', e);
        }
    });

    // Every list of the case, so the modal can show the price the saved line gets for its own date and
    // person; the invoice's own rates are the list in force today, which is what the range tooltip shows.
    // currentData, not data: after a move to another case the previous case's lists must not price the line.
    const { currentData: matterPricelists, isFetching: isFetchingMatterPricelists } = useFindMatterRatesQuery(
        matter?.id == null ? skipToken : String(matter.id)
    );

    // The lists price in the invoice currency, while the field shows the payment currency in that mode.
    const toDisplayedHourlyPrice = useCallback(
        (hourlyPrice: string) =>
            isPaymentCurrencyMode
                ? new Big(hourlyPrice).times(invoice.paymentCurrencyConversionRate ?? 1).toNumber()
                : Number(hourlyPrice),
        [isPaymentCurrencyMode, invoice.paymentCurrencyConversionRate]
    );

    // A price taken from the lists is only a preview, whatever was typed before: the field is not sent unless typed,
    // and the backend prices the saved line itself. An explicit reset (false) stays, so a date change after it
    // still re-prices the line on save.
    const forgetTypedHourlyPrice = useCallback(() => {
        if (getValues('isHourlyPriceManuallyAdjusted') === true) {
            setValue('isHourlyPriceManuallyAdjusted', null);
        }
    }, [getValues, setValue]);

    const updateHourlyPriceForEmployee = useCallback(
        (employee: EmployeeListItemMinDto, timecardDate: string) => {
            forgetTypedHourlyPrice();
            // The lists are missing only when the user may not read the case (the inputs wait for a fetch): the
            // field is cleared rather than left at a number the saved line will not get, as the backend prices it.
            if (!matterPricelists) {
                setValue('hourlyPrice', null);
                return;
            }
            const hourlyPrice = resolveHourlyPriceOn(
                matterPricelists,
                employee.id,
                employee.employmentHistory,
                timecardDate
            );
            setValue('hourlyPrice', hourlyPrice ? toDisplayedHourlyPrice(hourlyPrice) : null);
        },
        [setValue, forgetTypedHourlyPrice, matterPricelists, toDisplayedHourlyPrice]
    );

    // The new case's lists are not loaded yet; the backend prices the saved line from them.
    const handleMatterChange = useCallback(() => {
        forgetTypedHourlyPrice();
        setValue('hourlyPrice', null);
    }, [forgetTypedHourlyPrice, setValue]);

    const handleResetToOriginal = useCallback(() => {
        setValue('description', timecard.description || '');
        setValue('employee', timecard.employee);
        setValue('manualAdjustmentAmount', null);
        setValue('discountRate', 0);
        setNewEmployee(timecard.employee);
        updateHourlyPriceForEmployee(timecard.employee, getValues('timecardDate') || invoiceTimecardDate);
        // An explicit false asks the backend to re-price the line from the case's lists and clear the
        // hand-adjusted mark; null leaves the stored price alone and true is a typed price.
        setValue('isHourlyPriceManuallyAdjusted', false);
    }, [setValue, getValues, timecard, invoiceTimecardDate, updateHourlyPriceForEmployee]);

    const onHoursChange = useCallback(
        (hoursValue: string | undefined): void => {
            let secondsValue: number | null = 0;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }

            setValue('billedTimeInSeconds', secondsValue);
        },
        [setValue]
    );

    const calculatedTotalAmount = useMemo(() => {
        const hours = parseFloat(String(billedTimeInHours ?? '0').replace(',', '.')) || 0;
        const rate = Number(watchedHourlyPrice) || 0;
        const multiplier = parseFloat(String(watchedMultiplierRate ?? '1').replace(',', '.')) || 1;
        const discount = Number(watchedDiscountRate) || 0;
        const manualAdj = Number(watchedManualAdjustmentAmount) || 0;

        if (!hours || !rate) return null;

        const base = new Big(hours).times(rate).times(multiplier);
        const discounted = base.times(new Big(1).minus(new Big(discount).div(100)));
        return discounted.plus(manualAdj).toNumber();
    }, [
        billedTimeInHours,
        watchedHourlyPrice,
        watchedMultiplierRate,
        watchedDiscountRate,
        watchedManualAdjustmentAmount,
    ]);

    const [currentMatter, currentTimecardDate] = useWatch({
        control,
        name: ['matter', 'timecardDate'],
    });

    const canReadMatterAssignments = useCanReadMatterAssignments();

    const { data: currentMatterAssingments, isLoading: isLoadingMatterAssignments } =
        useGetMinAssignmentsListWithFiltersQuery(
            {
                filters: `matter.id==(${currentMatter?.id || '0'})`,
                sort: 'id,desc',
                pageSize: 1500,
            },
            { skip: !currentMatter || !canReadMatterAssignments }
        );

    const matterAssignmentOptions = useMemo<GroupedMatterAssignment[]>(() => {
        if (!currentMatter || !matterAssingmentList.length) {
            return [];
        }

        return (
            uniqBy([...(currentMatterAssingments?.content ?? [])], 'id')
                .map((assignment) => ({
                    ...assignment,
                    group: isAssignmentClosedOn(assignment, currentTimecardDate)
                        ? t('timecards.form.closed-assignments')
                        : t('timecards.form.active-assignments'),
                }))
                // Grouping ignores `sortDirection`, so the alphabetical order has to be established here.
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        );
    }, [currentMatter, currentMatterAssingments, matterAssingmentList, currentTimecardDate]);

    const assignmentGroupSortOrder = useMemo(
        () => [t('timecards.form.active-assignments'), t('timecards.form.closed-assignments')],
        []
    );

    useEffect(() => {
        // Without read access the options never load, so an assignment already on the timecard would
        // look like a stale selection and be dropped on save. Reconcile only against a real option list.
        if (!canReadMatterAssignments || isLoadingMatterAssignments) {
            return;
        }
        const currentValue = getValues('matterAssignment');
        if (
            (currentValue &&
                matterAssignmentOptions &&
                matterAssignmentOptions.some((option) => option.id === currentValue.id)) ||
            !currentValue
        ) {
            return;
        }
        setValue('matterAssignment', null);
    }, [canReadMatterAssignments, isLoadingMatterAssignments, matter, matterAssignmentOptions, getValues, setValue]);

    const handleMultiplierTypeCodeChange = useCallback(
        (value: string) => {
            setSelectedmultiplierType(value);
            const multiplierType = multiplierTypes.find((item) => item.value === value);
            if (multiplierType) {
                setValue('multiplierType', multiplierType);
                setValue('multiplierRate', multiplierType.multiplierRate);
            }
        },
        [multiplierTypes, setValue]
    );

    const handleEmployeeChange = useCallback(
        (employee: EmployeeListItemMinDto | null) => {
            if (employee) {
                setNewEmployee(employee);
                setValue('jobPositionName', employee.latestEmployment?.jobPositionName || '');
                updateHourlyPriceForEmployee(employee, getValues('timecardDate') || invoiceTimecardDate);
            }
        },
        [setValue, getValues, updateHourlyPriceForEmployee, invoiceTimecardDate]
    );

    // The backend re-prices a line whose date moved, as it does one whose person moved.
    const handleTimecardDateChange = useCallback(
        (timecardDate: Date | null) => {
            if (timecardDate) {
                updateHourlyPriceForEmployee(
                    newEmployee ?? timecard.employee,
                    format(timecardDate, DEFAULT_DATE_FORMAT)
                );
            }
        },
        [newEmployee, timecard.employee, updateHourlyPriceForEmployee]
    );

    const handlePositionTextCheckboxChange = useCallback(
        (checked: boolean) => {
            if (!checked) {
                setValue('positionText', '');
            }
        },
        [setValue]
    );

    const handleTimecardForCheckboxChange = useCallback(
        (checked: boolean) => {
            if (!checked) {
                setValue('positionText', '');
            }
        },
        [setValue]
    );

    const onPreserveNonBillableHoursChange = useCallback(
        (checked: boolean) => {
            const editIntentTypeCode = getValues('editIntentTypeCode');
            if (checked) {
                const totalTimeInSeconds = getValues('totalTimeInSeconds') ?? 0;
                setValue('billedTimeInSeconds', totalTimeInSeconds);
                setValue(
                    'billedTimeInHours',
                    String(timecardSecondsToHours(totalTimeInSeconds, isTwoDecimalEntryEnabled))
                );
            } else {
                setValue('billedTimeInHours', '0');
                if (editIntentTypeCode !== EditIntentTypeCode.MARK_AS_NONBILLABLE) {
                    setValue('billedTimeInSeconds', 0);
                }
            }
        },
        [getValues, setValue, isTwoDecimalEntryEnabled]
    );

    const handleIsNonBillableChange = useCallback(
        (checked: boolean) => {
            const preserveNonBillableHours = getValues('preserveNonBillableHours');
            if (checked) {
                setValue('editIntentTypeCode', EditIntentTypeCode.MARK_AS_NONBILLABLE);
                if (!preserveNonBillableHours) {
                    setValue('billedTimeInHours', '0');
                }
            } else {
                const totalTimeInSeconds = getValues('totalTimeInSeconds') ?? 0;
                setValue('billedTimeInSeconds', totalTimeInSeconds);
                setValue(
                    'billedTimeInHours',
                    String(timecardSecondsToHours(totalTimeInSeconds, isTwoDecimalEntryEnabled))
                );
                setValue('editIntentTypeCode', null);
                setValue('preserveNonBillableHours', false);
            }
        },
        [getValues, setValue, isTwoDecimalEntryEnabled]
    );

    if (isLoadingMatterAssignments) {
        return <Loader />;
    }

    const clientName = invoice.invoiceMatters?.find((item) => item.matterId === matter?.id)?.clientParty?.name;
    const matterField = (
        <DynamicSelectInput
            setValue={setValue}
            control={control}
            label="timecards.form.case-client"
            name="matter"
            getOptionLabel="fullName"
            paginated={{
                fetch: useLazyGetMattersListQuery,
                searchField: ['fullName', 'clientParty.name'],
                filters: mattersListFilter,
                isEnabled: matterIdList.length > 0,
                sort: ['clientParty.name,asc', 'name,asc'],
            }}
            groupByField="clientParty.name"
            getOptionIdentifier="id"
            placeholder="timecards.form.select-case"
            placeholderOnFocus="timecards.form.search-case-by"
            getSelectedValueStartAdornment={renderMatterIndicators}
            customOptionRenderer={renderMatterOption}
            onChange={handleMatterChange}
            required
            disabled={isSubmitting}
            // The case takes the row in Required, as the create dialog's case does.
            wrapperClassName={isNewDesign ? 'col-span-full' : undefined}
            bottomRightLabelComponent={
                // Inside the field, so the line follows it into the dialog's Required section.
                isNewDesign && clientName ? (
                    <div className="text-muted-foreground w-full text-xs">
                        {t('timecards.form.client')}: {clientName}
                    </div>
                ) : undefined
            }
        />
    );
    const assignmentField = (
        <SelectInput
            setValue={setValue}
            control={control}
            label="timecards.form.assignment"
            name="matterAssignment"
            placeholder="timecards.form.select-assignment"
            options={matterAssignmentOptions ?? []}
            getOptionLabel="name"
            getOptionIdentifier="id"
            sortDirection="asc"
            groupSortOrder={assignmentGroupSortOrder}
            disabled={!matterAssignmentOptions?.length || isSubmitting}
            clearable={isNewDesign ? false : undefined}
        />
    );
    const activityTypeField = (
        <SelectInput
            setValue={setValue}
            control={control}
            label="timecards.form.activity-type"
            name="timecardActivityType"
            placeholder="timecards.form.select-activity-type"
            options={
                (getValidOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as {
                    label: string;
                    value: string;
                    description: string;
                }[]) ?? []
            }
            getOptionLabel="label"
            getOptionIdentifier="value"
            required={true}
            clearable={isNewDesign ? false : undefined}
            disabled={isSubmitting}
        />
    );
    const descriptionField = (
        <TextAreaInput
            control={control}
            label="timecards.form.description"
            name="description"
            disabled={isSubmitting}
        />
    );
    const notesField = (
        <TextAreaInput
            control={control}
            label="timecards.form.notes"
            name="notes"
            maxLength={5000}
            disabled={isSubmitting}
        />
    );
    const nonBillableFields = (
        <div className="flex flex-row gap-4">
            <CheckboxInput
                control={control}
                name="isNonBillable"
                label="timecards.form.is-non-billable"
                onChange={handleIsNonBillableChange}
                disabled={isSubmitting}
            />
            {isNonBillable && !hideDontNullifyOption ? (
                <CheckboxInput
                    control={control}
                    name="preserveNonBillableHours"
                    label="timecards.preserve-non-billable-hours"
                    onChange={onPreserveNonBillableHoursChange}
                    rightLabelComponent={<InfoTooltip text={'timecards.tooltips.preserve-non-billable-hours'} />}
                    disabled={isSubmitting}
                />
            ) : null}
        </div>
    );
    const employeeField = (
        <DynamicSelectInput
            setValue={setValue}
            control={control}
            label="timecards.form.employee"
            name="employee"
            placeholder="timecards.form.select-employee"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            onChange={handleEmployeeChange}
            getOptionLabel="name"
            getOptionIdentifier="id"
            required
            disabled={isSubmitting || isFetchingMatterPricelists}
            clearable={isNewDesign ? false : undefined}
        />
    );
    const overwriteForCheckbox = (
        <CheckboxInput
            control={control}
            label={'timecards.form.overwrite-for'}
            name="isTimecardForInputEnabled"
            keepCheckbox
            onChange={handleTimecardForCheckboxChange}
            disabled={isSubmitting}
        />
    );
    const overwrittenNameField = (
        <TextInput
            control={control}
            label={'timecards.form.overwritten-name'}
            name="timecardForLabel"
            disabled={isSubmitting}
        />
    );
    const positionField = (
        <TextInput control={control} label={'invoices.details.table.position'} name="jobPositionName" disabled />
    );
    const positionCheckbox = (
        <CheckboxInput
            control={control}
            label={'invoices.details.table.position-checkbox-label'}
            name="isPositionTextInputEnabled"
            keepCheckbox
            onChange={handlePositionTextCheckboxChange}
            disabled={isSubmitting}
        />
    );
    const positionTextField = (
        <TextInput
            control={control}
            label={'invoices.details.table.position-text-label'}
            name="positionText"
            disabled={isSubmitting}
        />
    );
    const dateField = (
        <DateInput
            maxDate={new Date()}
            control={control}
            label="timecards.form.date"
            name="timecardDate"
            onChange={handleTimecardDateChange}
            required
            disabled={isSubmitting || isFetchingMatterPricelists}
        />
    );
    const remunerationField = (
        <RadioInput
            setValue={setValue}
            control={control}
            label="timecards.remuneration-time"
            name="remunerationCalcType"
            options={remunerationCalcTypeOptions}
            getOptionLabel="label"
            getOptionIdentifier="value"
            disabled={!isUserPrivilegedToUpdateRemunerationTime || isSubmitting}
        />
    );
    const remunerationHoursField =
        remunerationCalcType === RemunerationCalcType.MANUAL_ENTRY ? (
            <HoursInput
                control={control}
                name="remunerationTimeInHours"
                onChange={onRemunerationTimeChange}
                suffixComponent="h"
                disabled={!isUserPrivilegedToUpdateRemunerationTime || isSubmitting}
            />
        ) : null;
    const hourlyRateField = (
        <DecimalInput
            control={control}
            label={'invoices.details.table.hourly-rate'}
            name="hourlyPrice"
            disabled={isNonBillable || !isUserPrivilegedToUpdateHourlyRate || isSubmitting}
            onChange={() => {
                setValue('isHourlyPriceManuallyAdjusted', true);
            }}
            suffixComponent={isNewDesign ? `${displayCurrency}/h` : undefined}
        />
    );
    const usedHoursField = (
        <HoursInput
            control={control}
            label={isNewDesign ? 'timecards.form.total-time' : 'invoices.details.table.time-used'}
            name="totalTimeInHours"
            disabled
            suffixComponent="h"
        />
    );
    const billedField = (
        <HoursInput
            // pattern="^(\d{0,3}([\.\,]\d?)?)?$"
            control={control}
            label="invoices.details.table.billed"
            name="billedTimeInHours"
            onChange={(event) => onHoursChange(event.target.value)}
            disabled={(isNonBillable && !preserveNonBillableHours) || isSubmitting}
            suffixComponent="h"
            required
        />
    );
    const discountField = (
        <PercentageInput
            control={control}
            label="invoices.details.table.timecard-discount-pct"
            name="discountRate"
            required
            suffixComponent={isNewDesign ? '%' : undefined}
            onBlur={(value) => {
                if (!value) {
                    setValue('discountRate', 0);
                }
            }}
            disabled={isNonBillable || isSubmitting}
        />
    );
    const amountField = (
        <InputWrapper
            label={t('invoices.details.table.amount-currency', { currency: displayCurrency })}
            shouldTranslateLabel={false}
            name="total-amount"
            hasErrors={false}
        >
            <Input
                id="total-amount"
                className="bg-paper"
                value={calculatedTotalAmount ? new Big(Math.round(calculatedTotalAmount * 100) / 100).toFixed(2) : ''}
                disabled
                readOnly
            />
        </InputWrapper>
    );
    const manualAdjustmentField = (
        <DecimalInput
            control={control}
            label="invoices.details.table.manual-adjustment-amount"
            name="manualAdjustmentAmount"
            allowNegative
            disabled={isNonBillable || isSubmitting}
            suffixComponent={isNewDesign ? displayCurrency : undefined}
        />
    );
    const multiplierToggle = (
        <Controller
            control={control}
            name="multiplierType"
            render={() => (
                <ToggleGroup
                    value={selectedMultiplierType ? [selectedMultiplierType] : []}
                    onValueChange={(value: string[]) => handleMultiplierTypeCodeChange(value[0] ?? '')}
                    variant="outline"
                    size="sm"
                    spacing={0}
                    disabled={isNonBillable || isSubmitting}
                    aria-labelledby={isNewDesign ? multiplierLabelId : undefined}
                >
                    {multiplierTypes.map((type) => (
                        <ToggleGroupItem key={type.value} value={type.value}>
                            {type.label}
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>
            )}
        />
    );
    const mirroredCheckbox = (
        <CheckboxInput
            control={control}
            label={'invoices.details.table.is-mirrored-timecard-label'}
            name="isMirrored"
            keepCheckbox
            disabled={!isUserPrivilegedToEnableMirroring || isSubmitting}
        />
    );
    const hiddenCheckbox = (
        <CheckboxInput
            control={control}
            label={'invoices.details.table.is-hidden-timecard-label'}
            name="isHidden"
            keepCheckbox
            disabled={!isUserPrivilegedToEnableMirroring || isSubmitting}
        />
    );
    // A person, date or reset change prices the line from the lists, so they wait for them.
    const isResetDisabled = isSubmitting || isFetchingMatterPricelists;
    const paymentCurrencyClass = isPaymentCurrencyMode && paymentCurrencyInputClassName;

    if (isNewDesign) {
        const record = createRecord('timecard');
        return (
            <CreateDialogModalFrame
                icon={record.icon}
                tileClass={record.tileClass}
                title={
                    <FormattedMessage
                        id="create-dialog.edit-title"
                        values={{ record: t('create-dialog.record.timecard') }}
                    />
                }
                chip={
                    <>
                        <FormattedDate value={initialData.timecardDate} /> · {initialData.employee?.name}
                    </>
                }
                notice={isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
                openSection={INVOICE_SECTION}
                caption={
                    <FormattedMessage
                        id="invoices.details.table.reset-timecard-caption"
                        values={{ b: (chunks: ReactNode) => <strong className="font-medium">{chunks}</strong> }}
                    />
                }
                onCancel={handleCancel}
                cancelDisabled={isSubmitting}
                otherActions={
                    <Button {...CREATE_DIALOG_OTHER_ACTION} onClick={handleResetToOriginal} disabled={isResetDisabled}>
                        <FormattedMessage id="invoices.details.table.reset" />
                    </Button>
                }
                mainAction={{
                    label: t('save'),
                    onClick: () => {
                        void handleOnSave();
                    },
                    disabled: isSubmitting,
                }}
            >
                {/* Every field the timecard form has, in its order; the required ones are drawn in Required. */}
                <CreateDialogSection title={TIMECARD_SECTION}>
                    {matterField}
                    {matterAssignmentOptions?.length ? (
                        <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                            {assignmentField}
                        </FeatureFlagged>
                    ) : null}
                    {showActivityTypeSelectInput ? activityTypeField : null}
                    {employeeField}
                    {dateField}
                    {descriptionField}
                    {isTimecardNotesEnabled ? notesField : null}
                    {nonBillableFields}
                    {usedHoursField}
                    {showRemunerationCalcTypeRadioInput ? (
                        <div className="flex flex-wrap items-end gap-x-4">
                            <div className="min-w-0 flex-1">{remunerationField}</div>
                            {remunerationHoursField}
                        </div>
                    ) : null}
                </CreateDialogSection>
                <CreateDialogSection title={INVOICE_SECTION}>
                    {billedField}
                    {discountField}
                    {positionField}
                    {hourlyRateField}
                    {amountField}
                    {manualAdjustmentField}
                    <div className="flex flex-wrap gap-x-5">
                        {overwriteForCheckbox}
                        {positionCheckbox}
                    </div>
                    {isTimecardForInputEnabled ? overwrittenNameField : null}
                    {isPositionTextInputEnabled ? positionTextField : null}
                    <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_TIMECARD_EXTRA_OPTIONS}>
                        <>
                            <FeatureFlagged action="show" flag={FeatureFlag.TC_MULTIPLIER}>
                                {/* The segments keep their own width: the field would stretch them into a bar. */}
                                <Field className="*:w-auto">
                                    <FieldTitle id={multiplierLabelId}>
                                        {t('invoices.details.table.multiplier')}
                                    </FieldTitle>
                                    {multiplierToggle}
                                </Field>
                            </FeatureFlagged>
                            {/* The two options stack tight, one row each. */}
                            <div className="flex flex-col gap-1">
                                {mirroredCheckbox}
                                <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                                    {hiddenCheckbox}
                                </FeatureFlagged>
                            </div>
                        </>
                    </FeatureFlagged>
                </CreateDialogSection>
            </CreateDialogModalFrame>
        );
    }

    return (
        <>
            <ModalHeader
                title={t('invoices.details.invoice-timecard')}
                onCancel={handleCancel}
                subtitle={`(ID: ${initialData.id})`}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                {isPaymentCurrencyMode && <PaymentCurrencyNotice currency={displayCurrency} className="mb-4" />}
                <div className="grid grid-cols-12 gap-4">
                    <div className={FULL_ROW}>{matterField}</div>
                    {!!matterAssignmentOptions?.length && (
                        <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                            <div className={FULL_ROW}>{assignmentField}</div>
                        </FeatureFlagged>
                    )}
                    {showActivityTypeSelectInput && <div className={FULL_ROW}>{activityTypeField}</div>}

                    <div className={FULL_ROW}>{descriptionField}</div>
                    {isTimecardNotesEnabled && <div className={FULL_ROW}>{notesField}</div>}
                    <div className={FULL_ROW}>{nonBillableFields}</div>
                    <div className={FULL_ROW}>
                        {employeeField}
                        {overwriteForCheckbox}
                        {isTimecardForInputEnabled ? <div className="pt-2.5">{overwrittenNameField}</div> : null}
                    </div>
                    <div className={FULL_ROW}>{positionField}</div>
                    <div className="col-span-12 -mt-4">{positionCheckbox}</div>
                    {isPositionTextInputEnabled ? <div className={TUCKED_ROW}>{positionTextField}</div> : null}
                    <div className={isPositionTextInputEnabled ? FULL_ROW : TUCKED_ROW}>{dateField}</div>
                    {showRemunerationCalcTypeRadioInput && (
                        <div className="col-span-12 -mb-2 flex flex-wrap gap-x-4">
                            <div className="min-w-0 flex-1">{remunerationField}</div>
                            <div className="shrink-0 self-end">{remunerationHoursField}</div>
                        </div>
                    )}
                    <div className={cn('col-span-2', paymentCurrencyClass)}>{hourlyRateField}</div>
                    <div className="col-span-2">{usedHoursField}</div>
                    <div className="col-span-2">{billedField}</div>
                    <div className={cn('col-span-2', paymentCurrencyClass)}>{discountField}</div>
                    <div className={cn('col-span-4', paymentCurrencyClass)}>{amountField}</div>
                    <div className={cn('col-span-3', paymentCurrencyClass)}>{manualAdjustmentField}</div>
                    <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_TIMECARD_EXTRA_OPTIONS}>
                        <>
                            <FeatureFlagged action="show" flag={FeatureFlag.TC_MULTIPLIER}>
                                <div className={FULL_ROW}>{multiplierToggle}</div>
                            </FeatureFlagged>
                            <div className="col-span-12 -mt-2">{mirroredCheckbox}</div>
                            <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                                <div className="col-span-12 -mt-4">{hiddenCheckbox}</div>
                            </FeatureFlagged>
                        </>
                    </FeatureFlagged>
                    <div className={cn('col-span-12', isExtraOptionsShown && '-mt-2')}>
                        <hr className="border-divider" />
                    </div>
                    <div className="col-span-6">
                        <Tooltip text="invoices.details.table.reset-timecard-tooltip">
                            <Button
                                {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                                onClick={handleResetToOriginal}
                                disabled={isResetDisabled}
                            >
                                <FormattedMessage id="invoices.details.table.reset" />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button
                        key="cancel"
                        {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        {t('cancel')}
                    </Button>,
                    <Button
                        key="update-timecard"
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        onClick={() => {
                            void handleOnSave();
                        }}
                        disabled={isSubmitting}
                    >
                        {t('save')}
                    </Button>,
                ]}
            />
        </>
    );
};

export default InvoiceTimecardEditModal;
